import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSuitcase,
  faBriefcase,
  faBagShopping,
  faPlane,
  faPersonWalking,
} from "@fortawesome/free-solid-svg-icons";
import { airlines } from "../data/airlines";

function FlightDetailsModal({ flight, isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Flight Details</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
          </div>

          {/* Outbound Flight */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Outbound Flight</h3>
            <div className="space-y-4">
              {/* First Flight Segment */}
              <div className="flex items-start">
                <div className="flex-none w-1/4">
                  <p className="text-sm text-gray-500">
                    {flight.outbound_date}
                  </p>
                  <p className="text-xl font-bold">{flight.outbound_time}</p>
                  <p className="text-sm font-medium">{flight.origin}</p>
                  <p className="text-xs text-gray-500">
                    {flight.departure_terminal}
                  </p>
                </div>

                <div className="flex-grow px-4">
                  <div className="relative">
                    <div className="border-t-2 border-gray-300 w-full absolute top-4"></div>
                    <FontAwesomeIcon
                      icon={faPlane}
                      className="text-blue-600 absolute -top-2 right-0 transform -rotate-45"
                      size="lg"
                    />
                  </div>
                  <div className="text-center mt-6">
                    <p className="text-sm font-medium">{flight.duration}</p>
                    <div className="flex items-center justify-center space-x-2 mt-1">
                      <img
                        src={airlines[flight.operatorCode].logo}
                        alt={airlines[flight.operatorCode].name}
                        className="w-4 h-4 object-contain"
                      />
                      <p className="text-xs text-gray-600">
                        {airlines[flight.operatorCode].name} •{" "}
                        {flight.operatorCode}
                        {flight.flightNumber || "102"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-none w-1/4 text-right">
                  {flight.stopover ? (
                    <>
                      <p className="text-sm text-gray-500">
                        {flight.outbound_date}
                      </p>
                      <p className="text-xl font-bold">
                        {flight.stopover.arrival_time}
                      </p>
                      <p className="text-sm font-medium">
                        {flight.stopover.airport}
                      </p>
                      <p className="text-xs text-gray-500">
                        {flight.stopover.terminal}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">
                        {flight.outbound_arrival_date}
                      </p>
                      <p className="text-xl font-bold">
                        {flight.outbound_arrival_time}
                      </p>
                      <p className="text-sm font-medium">
                        {flight.destination}
                      </p>
                      <p className="text-xs text-gray-500">
                        {flight.arrival_terminal}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Connection/Stopover */}
              {flight.stopover && (
                <>
                  <div className="ml-1/4 pl-4 flex items-center">
                    <div className="bg-green-100 rounded-lg p-4 flex items-center w-full">
                      <FontAwesomeIcon
                        icon={faPersonWalking}
                        className="text-green-600 mr-3"
                      />
                      <div>
                        <p className="font-medium text-green-800">
                          Connection in {flight.stopover.airport}
                        </p>
                        <p className="text-sm text-green-600">
                          {flight.stopover.duration} connection
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Second Flight Segment */}
                  <div className="flex items-start">
                    <div className="flex-none w-1/4">
                      <p className="text-sm text-gray-500">
                        {flight.outbound_date}
                      </p>
                      <p className="text-xl font-bold">
                        {flight.stopover.departure_time}
                      </p>
                      <p className="text-sm font-medium">
                        {flight.stopover.airport}
                      </p>
                      <p className="text-xs text-gray-500">
                        {flight.stopover.terminal}
                      </p>
                    </div>

                    <div className="flex-grow px-4">
                      <div className="relative">
                        <div className="border-t-2 border-gray-300 w-full absolute top-4"></div>
                        <FontAwesomeIcon
                          icon={faPlane}
                          className="text-blue-600 absolute -top-2 right-0 transform -rotate-45"
                          size="lg"
                        />
                      </div>
                      <div className="text-center mt-6">
                        <p className="text-sm font-medium">
                          {flight.stopover.duration}
                        </p>
                        <div className="flex items-center justify-center space-x-2 mt-1">
                          <img
                            src={
                              airlines[
                                flight.stopover.operatorCode ||
                                  flight.operatorCode
                              ].logo
                            }
                            alt={
                              airlines[
                                flight.stopover.operatorCode ||
                                  flight.operatorCode
                              ].name
                            }
                            className="w-4 h-4 object-contain"
                          />
                          <p className="text-xs text-gray-600">
                            {
                              airlines[
                                flight.stopover.operatorCode ||
                                  flight.operatorCode
                              ].name
                            }{" "}
                            •{" "}
                            {flight.stopover.operatorCode ||
                              flight.operatorCode}
                            {flight.stopover.flightNumber || "131"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex-none w-1/4 text-right">
                      <p className="text-sm text-gray-500">
                        {flight.outbound_arrival_date}
                      </p>
                      <p className="text-xl font-bold">
                        {flight.outbound_arrival_time}
                      </p>
                      <p className="text-sm font-medium">
                        {flight.destination}
                      </p>
                      <p className="text-xs text-gray-500">
                        {flight.arrival_terminal}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Return Flight if exists - Similar structure as above */}
          {flight.return_date && (
            <div className="mb-8 border-t pt-8">
              <h3 className="text-lg font-semibold mb-4">Return Flight</h3>
              {/* Add similar structure for return flight */}
            </div>
          )}

          {/* Baggage Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">
              Baggage Information - {flight.baggage.fare_type} Fare
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon
                    icon={faBriefcase}
                    className="text-gray-600 mr-2"
                  />
                  <h4 className="font-medium">Personal Item</h4>
                </div>
                <p className="text-sm text-gray-600">
                  {flight.baggage.personal_item.dimensions}
                </p>
                <p className="text-sm text-green-600">
                  {flight.baggage.personal_item.included
                    ? "Included"
                    : "Not included"}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon
                    icon={faBagShopping}
                    className="text-gray-600 mr-2"
                  />
                  <h4 className="font-medium">Carry-on Bag</h4>
                </div>
                <p className="text-sm text-gray-600">
                  {flight.baggage.carry_on.dimensions}
                </p>
                <p className="text-sm text-green-600">
                  {flight.baggage.carry_on.included
                    ? "Included"
                    : "Not included"}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon
                    icon={faSuitcase}
                    className="text-gray-600 mr-2"
                  />
                  <h4 className="font-medium">Checked Bags</h4>
                </div>
                <p className="text-sm text-red-600">
                  {flight.baggage.checked_bags.included
                    ? "Included"
                    : "Not included"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlightDetailsModal;
